import { ClipboardModule } from '@angular/cdk/clipboard';
import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTabsModule } from '@angular/material/tabs';
import { FormlyModule } from '@ngx-formly/core';
import { FormlyMaterialModule } from '@ngx-formly/material';
import { FormlyMatDatepickerModule } from '@ngx-formly/material/datepicker';
import { PageEmptySearchResultComponent } from "../../../common-shared-module/components/page-empty-search-result/page-empty-search-result.component";
import { SectionLayoutMetadataBase } from '../../../common-shared-module/model/dyn-form/form-metadata/layout/section-layout.model';
import { Metadata } from '../../../common-shared-module/model/dyn-form/form-metadata/metadata.model';
import { ExtendedLayoutFormlyFieldGroup, FormlyFieldConfigFactory, FormlyFieldConfigRegistry, FormView, ItemFormlyFieldConfig, RowLayoutFormlyFieldGroup, SectionLayoutFormlyFieldGroup } from '../../../common-shared-module/model/dyn-form/form-visualizer/formly-group.model';
import { EFormWebServiceService } from '../../../common-shared-module/services/eform/e-form-webservice.service';
import { MetadataChange } from '../../models/metadata-change.model';

enum TabType {
    Preview = 0,
    Definition = 1,
    Value = 2,
    Schema = 3
}

@Component({
    selector: 'app-form-editor-section-preview',
    templateUrl: './form-editor-section-preview.component.html',
    styleUrl: './form-editor-section-preview.component.scss',
    standalone: true,
    imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    ClipboardModule,
    FormlyModule,
    FormlyMaterialModule,
    FormlyMatDatepickerModule,
    MatTabsModule,
    MatIconModule,
    MatButtonModule,
    PageEmptySearchResultComponent,
]
})
export class FormEditorSectionPreviewComponent implements OnInit {

    @Input() section: SectionLayoutMetadataBase | null = null

    lastSelected: ItemFormlyFieldConfig | ExtendedLayoutFormlyFieldGroup | null = null
    selectedTab: TabType = TabType.Preview

    sectionView!: SectionLayoutFormlyFieldGroup

    formlyRegistry: FormlyFieldConfigRegistry
    formlyFactory: FormlyFieldConfigFactory
    fv!: FormView

    customData!: Map<string, any>


    constructor(private readonly changeDetectorRef: ChangeDetectorRef, protected eFormWebServiceService: EFormWebServiceService) {
        this.formlyRegistry = new FormlyFieldConfigRegistry(this.eFormWebServiceService)
        this.formlyFactory = new FormlyFieldConfigFactory(this.formlyRegistry)

        this.customData = new Map<string, any>()
        
        this.formlyRegistry.setCustomDataWebService(this.customData)
    }


    ngOnInit(): void {
        this.sectionView = new SectionLayoutFormlyFieldGroup(<SectionLayoutMetadataBase>this.section, this.formlyFactory, 'form-editor')
        this.fv = new FormView(this.sectionView)
        this.fv.fields.push(this.sectionView)
    }

    showSelected(newSelected: Metadata) {
        this.clearSelection()
        let element = this.formlyRegistry.get(newSelected.id)
        if (element && (element instanceof ItemFormlyFieldConfig || element instanceof ExtendedLayoutFormlyFieldGroup)) {
            this.lastSelected = element
            this.lastSelected.showSelected()
        }
    }

    moverScroll() {

        if (this.lastSelected == null) return

        let element = document.getElementById('' + this.lastSelected.id)
        if (element == null) return

        element.scrollIntoView({
            behavior: "auto",
            block: "center",
            inline: 'nearest'
        });
    }

    clearSelection() {
        if (this.lastSelected) {
            this.lastSelected.clearSelection()
        }
        this.lastSelected = null
    }

    refreshAll(section: SectionLayoutMetadataBase) {
        this.section  = section
        this.ngOnInit()
    }

    refreshChange(change: MetadataChange) {

        change.changed.forEach(c => {
            let element = this.formlyRegistry.get(c.id)
            if (element instanceof SectionLayoutFormlyFieldGroup) {
                element.refresh()
                element.inflate()
                return
            }
            if (element instanceof ExtendedLayoutFormlyFieldGroup) {
                element.refresh()
                element.inflate()
                return
            }

            if (element instanceof RowLayoutFormlyFieldGroup) {
                element.refresh()
                element.inflate()
                return
            }

            if (element instanceof ItemFormlyFieldConfig) {
                element.refresh()
                return
            }

        })

        change.deleted.forEach(c => {
            this.formlyRegistry.delete(c.id)
        })

        this.fv = new FormView(this.sectionView)
        this.fv.fields.push(this.sectionView)

        this.clean()
        this.changeDetectorRef.detectChanges()
    }

    clean() {
        this.formlyRegistry = new FormlyFieldConfigRegistry(this.eFormWebServiceService)
        this.formlyFactory = new FormlyFieldConfigFactory(this.formlyRegistry)

        this.formlyRegistry.setCustomDataWebService(this.customData)

        this.sectionView = new SectionLayoutFormlyFieldGroup(<SectionLayoutMetadataBase>this.section, this.formlyFactory, 'form-editor')

        this.fv = new FormView(this.sectionView)
        this.fv.fields.push(this.sectionView)

        if (this.lastSelected) {
            this.showSelected({ id: "" + this.lastSelected.id, type: "" })

            setTimeout(() => {
                this.moverScroll()
            }, 100)
        }
    }

    reset(): void {
        this.fv.form.reset()
    }

    toSchema(): string {
        return this.section ? this.section.schema() : ''
    }

    prettyPrint(obj: any): string {
        if (obj == null) return ''

        let jsonLine = /^( *)("[\w]+": )?("[^"]*"|[\w.+-]*)?([,[{])?$/mg
        return JSON.stringify(obj, null, 3)
            .replace(/&/g, '&amp;').replace(/\\"/g, '&quot;')
            .replace(/</g, '&lt;').replace(/>/g, '&gt;')
            .replace(jsonLine, this.replacer)
    }

    replacer(match: any, pIndent: any, pKey: any, pVal: any, pEnd: any): string {
        let key = '<span class=json-key>';
        let val = '<span class=json-value>'
        let str = '<span class=json-string>'
        let r = pIndent || ''

        if (pKey) r = r + key + pKey.replace(/[": ]/g, '') + '</span>: '
        if (pVal) r = r + (pVal[0] == '"' ? str : val) + pVal + '</span>'

        return r + (pEnd || '')
    }

    submit() {}

    changeTab(tabIndex: number) {
        this.selectedTab = tabIndex
    }

}
